import React, {useEffect, useMemo, useState} from "react"
import {BackButton} from "../../components/BackButton/BackButton"
import {useHistory, useLocation} from "react-router-dom"
import {useFormik} from "formik"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import * as yup from "yup"
import {Helmet} from "react-helmet-async"
import {Button, Checkbox, Form, Tag} from "antd"
import {FormInput} from "../../components/Form/FormInput"
import {FormTextarea} from "../../components/Form/FormTextarea"
import {getObjectGroups, setObjectGroups} from "../../store/objectGroupReducer"
import {getCompanies, setCompanies} from "../../store/companyReducer"
import {FormSelect} from "../../components/Form/FormSelect"
import {changeObject, createObject} from "../../store/objectReducer"
import {JsonFormValues} from "../../components/Form/JSON/JsonFormValues"
import {securityObjectOptions} from "../../helps/optionsSelect"
import {ObjectResponse} from "../../models/response"

const schema = yup.object().shape({
    name: yup.string().required('Введите название').max(250, 'Максимальная длина 250 символов'),
    idCompany: yup.number().required('Выберите компанию').min(1, 'Выберите компанию'),
    idObjectGroup: yup.number().required('Выберите группу').min(1, 'Выберите группу'),
    description: yup.string().max(2000, 'Максимальная длина 2000 символов'),
    attributes: yup.string().required('Введите атрибуты').max(5000, 'Максимальная длина 5000 символов'),
})

export const ObjectForm = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation<{ object: ObjectResponse, idCompany?: number, idObjectGroup?: number }>()
    const object = location?.state?.object
    const idCompany = location?.state?.idCompany
    const idObjectGroup = location?.state?.idObjectGroup
    const status = useSelector((state: StateType) => state.appReducer.status)
    const companies = useSelector((state: StateType) => state.companyReducer.companies)
    const objectGroups = useSelector((state: StateType) => state.objectGroupReducer.objectGroups)
    const [submitCallbacks, setSubmitCallbacks] = useState<any>(null)
    const controller = useMemo(() => new AbortController(), [])

    useEffect(() => {
        dispatch(getCompanies({excludeSystem: true, controller}))

        return () => {
            controller.abort()
            dispatch(setCompanies(null))
            dispatch(setObjectGroups(null))
        }
    }, [dispatch, controller])

    const formik = useFormik({
        initialValues: {
            name: object?.name || '',
            address: object?.address || '',
            idCompany: object?.idCompany || idCompany,
            idObjectGroup: object?.idObjectGroup || idObjectGroup,
            active: object ? object.active : true,
            description: object?.description || '',
            attributes: object?.attributes || '[]',
            securityType: object?.securityType || securityObjectOptions[0].value,
            securityActive: object ? object?.securityActive : true,
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            const submit = async () => {
                let res: any
                if (object) {
                    res = await dispatch(changeObject({
                        id: object.id,
                        name: values.name,
                        address: values.address,
                        idObjectGroup: values.idObjectGroup || 0,
                        active: values.active,
                        description: values.description,
                        attributes: values.attributes,
                        securityType: values.securityType,
                        securityActive: values.securityActive,
                        controller,
                    }))
                } else {
                    res = await dispatch(createObject({
                        name: values.name,
                        address: values.address,
                        idObjectGroup: values.idObjectGroup || 0,
                        active: values.active,
                        description: values.description,
                        attributes: values.attributes,
                        securityType: values.securityType,
                        securityActive: values.securityActive,
                        controller,
                    }))
                }
                if (res?.payload?.status === 0) {
                    if (!object)
                    {
                        history.replace(`/object/${res.payload.data.id}`)
                    }
                    else
                    {
                        history.goBack()
                    }
                }
            }

            if (submitCallbacks) {
                const result = await submitCallbacks()
                if (result) await submit()
            } else await submit()
        },
    })
    const {values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue} = formik

    useEffect(() => {
        if (values?.idCompany) {
            dispatch(getObjectGroups({idCompany: values.idCompany, controller}))
        }
    }, [dispatch, values?.idCompany, controller])

    useEffect(() => {
        if (companies?.data && !values?.idCompany) {
            setFieldValue('idCompany', companies.data[0]?.id)
        }

        if (objectGroups?.data && !values?.idObjectGroup) {
            setFieldValue('idObjectGroup', objectGroups.data[0]?.id)
        }
    }, [companies, objectGroups, values])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({value: i.id, label: i.name})), [companies])
    const groupsOptions = useMemo(() => objectGroups?.data?.map(i => ({value: i.id, label: i.name})), [objectGroups])

    return (
        <div className="form-page">
            <Helmet>
                <title>{object ? 'Обновление объекта' : 'Новый объект'}</title>
            </Helmet>
            <Form className="form-container"
                  autoComplete="off"
                  onSubmitCapture={handleSubmit}
            >
                <div className="description-header"><BackButton onClick={() => history.goBack()}/></div>

                <div className="description-title">{object ? 'Обновление объекта' : 'Новый объект'}</div>

                <FormInput placeholder="Название"
                           value={values.name}
                           name="name"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.name && errors.name}
                           allowClear
                />
                <FormInput placeholder="Адрес"
                           value={values.address}
                           name="address"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.address && errors.address}
                           allowClear
                />
                <FormSelect placeholder="Компания"
                            value={values.idCompany || ''}
                            onChange={(value) => {
                                dispatch(setObjectGroups(null))
                                setFieldValue('idCompany', value)
                                setFieldValue('idObjectGroup', null)
                            }}
                            options={companyOptions}
                            className="form-select"
                            isInvalid={touched.idCompany && errors.idCompany}
                />
                <FormSelect placeholder="Группа"
                            value={values.idObjectGroup || ''}
                            onChange={(value) => setFieldValue('idObjectGroup', value)}
                            options={groupsOptions}
                            className="form-select"
                            isInvalid={touched.idObjectGroup && errors.idObjectGroup}
                />
                <FormSelect placeholder="Способ постановки на охрану"
                            value={values.securityType}
                            onChange={(value) => setFieldValue('securityType', value)}
                            options={securityObjectOptions}
                            className="form-select"
                />
                <FormTextarea placeholder="Описание"
                              value={values.description}
                              name="description"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              isInvalid={touched.description && errors.description}
                              allowClear
                              autoSize={{minRows: 2}}
                />
                <JsonFormValues attributes={object?.attributes}
                                rules={objectGroups?.data?.find(i => i.id === values.idObjectGroup)?.attributes}
                                setAttributes={(value) => setFieldValue('attributes', value)}
                                setSubmitCallbacks={setSubmitCallbacks}
                />

                <div style={{marginTop: 20}}>
                    <Checkbox checked={values.active}
                              name="active"
                              onChange={handleChange}
                    >
                        <Tag color={values.active ? 'green' : 'default'}>Активно</Tag>
                    </Checkbox>
                </div>

                <div style={{marginTop: 20}}>
                    <Checkbox checked={values.securityActive}
                              name="securityActive"
                              onChange={handleChange}
                    >
                        <Tag color={values.securityActive ? 'green' : 'default'}>Охрана активна</Tag>
                    </Checkbox>
                </div>

                <div className="form-button-block">
                    <Button type="primary"
                            htmlType="submit"
                            loading={status === 'loading'}
                    >
                        Сохранить
                    </Button>
                    <Button type="default"
                            onClick={() => history.goBack()}
                    >
                        Отмена
                    </Button>
                </div>
            </Form>
        </div>
    )
}

